import type { BotDifficulty, BotPersonalitySelection } from "../bot-types.js";
import { topology } from "../content/setup.js";
import type { ConflictConfig, RoomOrderAck, RoomRole } from "../index.js";
import { decodeOnlineRoomSave } from "../online-room-save.js";
import { OnlineRoomSession, type OnlineRoomSnapshot } from "../online-room-session.js";
import { autosaveSingleHuman, renderOnline } from "./online-room-view.js";

interface OnlineDebug {
  snapshot(): OnlineRoomSnapshot;
  peerStates(): readonly string[];
  ready(value?: boolean): Promise<void>;
  start(): Promise<void>;
  order(targetSeatId?: string): Promise<RoomOrderAck>;
  rawOrder(value: unknown): Promise<RoomOrderAck>;
  pause(paused: boolean): Promise<void>;
  approvePause(): Promise<void>;
  handoff(nextHostMemberId: string): Promise<void>;
  exportSave(): string;
}

declare global {
  interface Window {
    __kilnfrontOnline?: OnlineDebug;
  }
}

export function mountOnlineRoom(root: HTMLElement, parameters: URLSearchParams): () => void {
  const roomId = requiredParameter(parameters, "room");
  const role = roomRole(parameters.get("role"));
  const identity = persistentIdentity(roomId, role);
  const relayUrl = parameters.get("relay") || undefined;
  const resume = role === "host" ? resumedRoom(roomId, parameters) : undefined;
  const config =
    role === "host"
      ? { ...(resume?.config ?? roomConfig(roomId, parameters)), matchId: roomId }
      : undefined;
  const session = new OnlineRoomSession({
    roomId,
    role,
    sessionId: identity.sessionId,
    reconnectToken: identity.reconnectToken,
    ...(config ? { config } : {}),
    ...(resume ? { resume } : {}),
    ...(relayUrl ? { relayUrl } : {}),
    ...(location.hostname === "127.0.0.1" && parameters.get("incompatible") === "1"
      ? { testOnlyContentDigest: "sha256:incompatible-test-build" }
      : {}),
  });
  const viewState = { ready: false, status: "", chatDraft: "", roomSave: "" };
  const submitTarget = async (targetSeatId?: string) => {
    const snapshot = session.getSnapshot();
    const target =
      targetSeatId ??
      snapshot.state?.observation.seats.find((seat) => seat.id !== snapshot.seatId)?.id;
    if (!target) throw new TypeError("No opponent is available");
    const result = await session.submit({
      requestId: `${identity.sessionId}:${Date.now()}`,
      expectedScopeGeneration: "0",
      intent: { kind: "set-target", targetSeatId: target },
    });
    viewState.status = result.ok
      ? `Order admitted for ${result.seatId} at ${result.assignedBoundaryMs}ms.`
      : `Order rejected: ${result.reason}`;
    render(session.getSnapshot());
    return result;
  };
  window.__kilnfrontOnline = debugApi(session, submitTarget);
  const render = (snapshot: OnlineRoomSnapshot) =>
    renderOnline(
      root,
      snapshot,
      session,
      submitTarget,
      roomId,
      role,
      identity.sessionId,
      relayUrl,
      viewState,
    );
  const unsubscribe = session.subscribe((snapshot) => {
    render(snapshot);
    autosaveSingleHuman(session, snapshot, roomId);
  });
  render(session.getSnapshot());
  return () => {
    unsubscribe();
    delete window.__kilnfrontOnline;
    void session.dispose();
  };
}

function debugApi(
  session: OnlineRoomSession,
  submitTarget: (targetSeatId?: string) => Promise<RoomOrderAck>,
): OnlineDebug {
  return {
    snapshot: () => session.getSnapshot(),
    peerStates: () => Object.values(session.peerConnections()).map((peer) => peer.connectionState),
    ready: async (value = true) => session.setReady(value),
    start: async () => session.start(),
    order: submitTarget,
    rawOrder: (value) => session.submit(value as Parameters<OnlineRoomSession["submit"]>[0]),
    pause: (paused) => session.requestPause(paused),
    approvePause: () => session.approvePause(),
    handoff: (nextHostMemberId) => session.handoff(nextHostMemberId),
    exportSave: () => session.exportSave(),
  };
}

function resumedRoom(roomId: string, parameters: URLSearchParams) {
  if (parameters.get("resume") !== "1") return undefined;
  const raw = sessionStorage.getItem(`kilnfront-room-resume:${roomId}`);
  if (!raw) throw new TypeError("The retained online room save is unavailable in this tab");
  return decodeOnlineRoomSave(raw);
}

function roomConfig(roomId: string, parameters: URLSearchParams): ConflictConfig {
  const ids = ["host", "guest", "clockwork"];
  const edges = topology("ring", ids);
  const mode = parameters.get("mode") === "team-shift" ? "team-shift" : "every-town-for-itself";
  const bot = {
    personality: botPersonality(parameters.get("bot")),
    difficulty: botDifficulty(parameters.get("difficulty")),
  };
  return {
    matchId: roomId,
    rootSeed: [...new TextEncoder().encode(`kilnfront-room-${roomId}`)]
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join(""),
    mode,
    mapId: "ring",
    durationPreset: "lunch-shift",
    seats: ids.map((id, index) => ({
      id,
      name: index === 0 ? "Host Foundry" : index === 1 ? "Guest Foundry" : "Clockwork Foundry",
      adjacentTo: edges.flatMap(([left, right]) =>
        left === id ? [right] : right === id ? [left] : [],
      ),
      bot,
      ...(mode === "team-shift" ? { teamId: index < 2 ? "human" : "clockwork" } : {}),
    })),
  };
}

function botPersonality(value: string | null): BotPersonalitySelection {
  if (
    value === "builder" ||
    value === "defender" ||
    value === "raider" ||
    value === "siege-planner" ||
    value === "broker"
  )
    return value;
  return "seeded-mix";
}

function botDifficulty(value: string | null): BotDifficulty {
  if (value === "apprentice" || value === "superintendent") return value;
  return "foreman";
}

function persistentIdentity(roomId: string, role: RoomRole) {
  const key = `kilnfront-room:${roomId}:${role}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return JSON.parse(existing) as { sessionId: string; reconnectToken: string };
  const value = {
    sessionId: `${role}-${crypto.randomUUID().slice(0, 8)}`,
    reconnectToken: crypto.randomUUID(),
  };
  sessionStorage.setItem(key, JSON.stringify(value));
  return value;
}

function requiredParameter(parameters: URLSearchParams, name: string): string {
  const value = parameters.get(name)?.trim();
  if (!value || value.length > 64) throw new TypeError(`Invalid ${name}`);
  return value;
}

function roomRole(value: string | null): RoomRole {
  if (value === "host" || value === "controller" || value === "spectator") return value;
  throw new TypeError("Online room role must be host, controller, or spectator");
}
